import { Bell, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetEmailPreferences, useUpdateEmailPreferences } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

export function EmailPreferencesCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["email-preferences", user?.id];
  const { data, isLoading, isError, refetch } = useGetEmailPreferences({
    query: { queryKey, enabled: !!user?.id, refetchOnWindowFocus: false },
  });
  const update = useUpdateEmailPreferences({
    mutation: {
      onSuccess: (preferences) => {
        queryClient.setQueryData(queryKey, preferences);
        toast({ title: "Email preferences saved" });
      },
      onError: () => toast({
        title: "Couldn't save your choice",
        description: "Please try again. Your previous setting is still in place.",
        variant: "destructive",
      }),
    },
  });

  const toggle = (preference: "dailySummary" | "lowStock", checked: boolean) => {
    update.mutate({ data: preference === "dailySummary"
      ? { dailySummary: checked }
      : { lowStock: checked } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="w-4 h-4" /> Email notifications
        </CardTitle>
        <CardDescription>Choose which optional store updates reach your inbox.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading email preferences…
          </p>
        ) : isError || !data ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <p className="text-destructive">Email preferences could not be loaded.</p>
            <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>Retry</Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <label htmlFor="daily-summary-email" className="cursor-pointer space-y-1">
                <span className="block text-sm font-medium">Daily business summaries</span>
                <span className="block text-xs text-muted-foreground">Yesterday's sales and expenses by email.</span>
              </label>
              <Switch
                id="daily-summary-email"
                checked={data.dailySummary}
                onCheckedChange={(checked) => toggle("dailySummary", checked)}
                disabled={update.isPending}
                aria-label="Daily business summary emails"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <label htmlFor="low-stock-email" className="cursor-pointer space-y-1">
                <span className="block text-sm font-medium">Low-stock alerts</span>
                <span className="block text-xs text-muted-foreground">An email when a product reaches its reorder level.</span>
              </label>
              <Switch
                id="low-stock-email"
                checked={data.lowStock}
                onCheckedChange={(checked) => toggle("lowStock", checked)}
                disabled={update.isPending}
                aria-label="Low-stock alert emails"
              />
            </div>
            <p className="border-t pt-4 text-xs text-muted-foreground">
              Account security, receipts, orders and subscription emails are not affected by these choices.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}